import { createContext, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Open state of the employee notification panel, shared by its two openers on
 * the employee (ESS) surface: the bell in `EmployeeTopBar` and the
 * "Notifications" row on the Profile screen.
 *
 * One panel, two ways in — the Profile row opens the SAME
 * `EmployeeNotificationBell` panel rather than a second notifications list,
 * so the list, the unread count and "opening a notice dismisses it" can never
 * differ between the two (`docs/design-system/CLAUDE.md` § "One canonical
 * component per status/badge concept").
 *
 * `null` outside `EssNotificationsProvider`: the bell then keeps its own
 * uncontrolled state, exactly as before this existed.
 */
export interface EssNotificationsControl {
  open: boolean;
  set_open: (open: boolean) => void;
  /**
   * Open the panel from somewhere other than the bell, and send focus back to
   * `opener` when it closes. Without this, Radix returns focus to the bell's
   * trigger in the app bar, dropping a keyboard or screen-reader user at the
   * top of the page instead of back on the row they came from.
   */
  open_from: (opener: HTMLElement) => void;
  /** The element to refocus on close, consumed (cleared) by the bell. */
  take_return_focus: () => HTMLElement | null;
}

const EssNotificationsContext = createContext<EssNotificationsControl | null>(
  null,
);

export function EssNotificationsProvider({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element {
  const [open, set_open] = useState(false);
  const return_focus = useRef<HTMLElement | null>(null);
  const control = useMemo<EssNotificationsControl>(
    () => ({
      open,
      set_open,
      open_from: (opener) => {
        return_focus.current = opener;
        set_open(true);
      },
      take_return_focus: () => {
        const opener = return_focus.current;
        return_focus.current = null;
        return opener;
      },
    }),
    [open],
  );
  return (
    <EssNotificationsContext.Provider value={control}>
      {children}
    </EssNotificationsContext.Provider>
  );
}

export function useEssNotificationsControl(): EssNotificationsControl | null {
  return useContext(EssNotificationsContext);
}
